import {
  DISPLAY_SCREEN_TYPE,
  getActiveQrPayment,
  getMediaUrlCandidates,
  getSortedMedia,
  parseMediaSplitPercentage,
  PaymentDetail,
  ResolvedMediaItem,
  SUCCESS_VIEW_HOLD_MS,
} from "@rista/core/customerDisplay";
import { useImageUrlFallback } from "@rista/core/hooks/useImageUrlFallback";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Animated,
  ScrollView,
} from "react-native";




export const usePaymentDetails = (paymentDetails?: Map<string, PaymentDetail>) => {
  const qrPayment = useMemo(
    () => getActiveQrPayment(paymentDetails),
    [paymentDetails],
  );

  return {
    qrPayment,
  };
};


/**
 * Seconds remaining, ticking down to zero once a second.
 *
 * `resetKey` restarts it — the display re-renders on every payload push, so
 * without one a cart edit mid-payment would leave the clock running from a
 * component that never remounted; with it, a genuinely new QR starts over.
 */
export const useCountdown = (totalSeconds: number, resetKey?: string) => {
  const [remaining, setRemaining] = useState(totalSeconds);

  useEffect(() => {
    setRemaining(totalSeconds);
  }, [totalSeconds, resetKey]);

  useEffect(() => {
    if (remaining <= 0) {
      return;
    }

    const timer = setTimeout(() => {
      setRemaining((seconds) => (seconds > 0 ? seconds - 1 : 0));
    }, 1000);

    return () => clearTimeout(timer);
  }, [remaining]);

  return remaining;
};


/**
 * True while the "order successfully placed" screen should stay up.
 *
 * The success screen cannot be driven off the sale: a checkout success resets the cart in the
 * same tick, and an empty cart is the idle screen — so the closed sale is gone before anything
 * renders it. `saleClosedAt` is the POS's record that a sale closed (customerDisplayStore),
 * and this holds the display in its order state for SUCCESS_VIEW_HOLD_MS from that moment.
 *
 * A timestamp, not a flag, so the window is measured from when the sale actually closed: a
 * display that mounts (or replays the cached cart) near the end of it gets the remainder, and
 * one that mounts after it gets nothing rather than a stale success screen.
 */
export const useOrderSuccessHold = (
  saleClosedAt?: number,
  holdMs: number = SUCCESS_VIEW_HOLD_MS,
) => {
  const [isHolding, setIsHolding] = useState(false);

  useEffect(() => {
    if (!saleClosedAt) {
      return;
    }

    const remaining = holdMs - (Date.now() - saleClosedAt);

    if (remaining <= 0) {
      setIsHolding(false);
      return;
    }

    setIsHolding(true);

    const timer = setTimeout(() => setIsHolding(false), remaining);

    return () => clearTimeout(timer);
    // Only a NEW close re-arms this. Every payload push re-renders the display, but
    // saleClosedAt is a number, so an unrelated push leaves the running timer alone.
  }, [saleClosedAt, holdMs]);

  return isHolding;
};


/**
 * Walks a media item's URL candidates, best first, stepping down to the next
 * whenever one fails to load — see getMediaUrlCandidates. Both MediaSlide
 * variants share this; only the element reporting the error differs (<img>
 * on desktop, <Image> on native).
 *
 * The stepping itself moved to `@rista/core/hooks/useImageUrlFallback`
 * (2026-09-09) so the kiosk's item artwork walks the same candidates through
 * the same code. What stays here is the media-item shape: this hook still
 * takes a `ResolvedMediaItem`, so no MediaSlide changed.
 *
 * One behaviour difference came with the move: the core hook steps PAST the
 * last candidate instead of clamping to it, and reports `exhausted`. Neither
 * MediaSlide reads that yet — they render `url`, which is then `undefined`
 * rather than a URL already known to fail. `<img src={undefined}>` and
 * `<Image source={{ uri: undefined }}/>` both draw nothing, which is what a
 * campaign slide with no working derivative should do.
 */
export const useMediaUrlFallback = (item: ResolvedMediaItem) =>
  useImageUrlFallback(getMediaUrlCandidates(item));


export const useDisplayMedia = (campaignConfig: any, staticMediaBaseUrl?: string) => {
  const displays = campaignConfig?.displayObject ?? [];
  const orientation = campaignConfig?.mediaOrientation || "landscape";
  const idleDisplay = displays.find(
    (display: any) =>
      display?.screenType === DISPLAY_SCREEN_TYPE.IDLE,
  );

  const orderDisplay = displays.find(
    (display: any) =>
      display?.screenType === DISPLAY_SCREEN_TYPE.ORDER,
  );

  const idleMedia = getSortedMedia(idleDisplay, staticMediaBaseUrl);
  const orderMedia = getSortedMedia(orderDisplay, staticMediaBaseUrl);

  const mediaSplitTarget = parseMediaSplitPercentage(
    orderDisplay?.split,
    50,
  );

  return {
    idleMedia,
    orderMedia,

    hasIdleMedia: idleMedia.length > 0,
    hasOrderMedia: orderMedia.length > 0,

    mediaSplitTarget,
    orderSplitTarget: 100 - mediaSplitTarget,

    idleDwellSeconds: Number(idleDisplay?.dwellSeconds) || 1,
    orderDwellSeconds: Number(orderDisplay?.dwellSeconds) || 1,

    orientation,
  };
};


const ANIMATION_DURATION = 400;
const CART_INITIAL_TRANSLATE = 200;

export const useCustomerDisplayAnimation = ({
  hasItems,
  hasOrderMedia,
  mediaSplitTarget,
  orderSplitTarget,
  orientation,
}: {
  hasItems: boolean;
  hasOrderMedia: boolean;
  mediaSplitTarget: number;
  orderSplitTarget: number;
  orientation: "landscape" | "portrait";
}) => {
  const progress = useRef(
    new Animated.Value(hasItems ? 1 : 0),
  ).current;

  useEffect(() => {
    Animated.timing(progress, {
      toValue: hasItems ? 1 : 0,
      duration: ANIMATION_DURATION,
      useNativeDriver: false,
    }).start();
  }, [hasItems, progress]);

  const isPortrait = orientation === "portrait";

  const effectiveMediaSplit = hasOrderMedia
    ? mediaSplitTarget
    : 0;

  const effectiveOrderSplit = hasOrderMedia
    ? orderSplitTarget
    : 100;

  const animatedMediaSize = progress.interpolate({
    inputRange: [0, 1],
    outputRange: [
      "100%",
      `${effectiveMediaSplit}%`,
    ],
  });

  const animatedCartSize = progress.interpolate({
    inputRange: [0, 1],
    outputRange: [
      "0%",
      `${effectiveOrderSplit}%`,
    ],
  });

  const animatedCartTranslate = progress.interpolate({
    inputRange: [0, 1],
    outputRange: [
      CART_INITIAL_TRANSLATE,
      0,
    ],
  });

  return {
    // in landscape the media/cart split is a width; in portrait it's a height.
    // `as const` keeps the literal from widening to `string`: RN's style types accept
    // `${number}%`, so a widened `string` fails to typecheck at every use site.
    animatedMediaWidth: isPortrait ? ("100%" as const) : animatedMediaSize,
    animatedMediaHeight: isPortrait ? animatedMediaSize : ("100%" as const),

    animatedCartWidth: isPortrait ? ("100%" as const) : animatedCartSize,
    animatedCartHeight: isPortrait ? animatedCartSize : ("100%" as const),

    // cart slides in from the right in landscape, from the bottom in portrait
    animatedCartTranslateX: isPortrait ? 0 : animatedCartTranslate,
    animatedCartTranslateY: isPortrait ? animatedCartTranslate : 0,

    animatedCartOpacity: progress.interpolate({
      inputRange: [0, 0.2, 1],
      outputRange: [0, 0.3, 1],
    }),
  };
};

const LOOP_RESET_DELAY = 300;

export const useSlideshow = ({
  media,
  dwellSeconds,
}: {
  media: any[];
  dwellSeconds: number;
}) => {
  const scrollViewRef = useRef<ScrollView>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);

  const slideshowMedia = useMemo(
    () => (media.length > 1 ? [...media, media[0]] : media),
    [media],
  );

  useEffect(() => {
    setCurrentIndex(0);

    scrollViewRef.current?.scrollTo({
      x: 0,
      y: 0,
      animated: false,
    });
  }, [media]);

  useEffect(() => {
    if (media.length <= 1 || containerWidth <= 0) {
      return;
    }

    const duration = Math.max(Number(dwellSeconds) || 1, 1) * 1000;

    const timer = setTimeout(() => {
      const nextIndex = currentIndex + 1;
      const isLastSlide = nextIndex === media.length;

      scrollViewRef.current?.scrollTo({
        x: nextIndex * containerWidth,
        y: 0,
        animated: true,
      });

      if (isLastSlide) {
        setTimeout(() => {
          scrollViewRef.current?.scrollTo({
            x: 0,
            y: 0,
            animated: false,
          });

          setCurrentIndex(0);
        }, LOOP_RESET_DELAY);

        return;
      }

      setCurrentIndex(nextIndex);
    }, duration);

    return () => clearTimeout(timer);
  }, [
    currentIndex,
    media.length,
    dwellSeconds,
    containerWidth,
  ]);

  return {
    scrollViewRef,
    containerWidth,
    setContainerWidth,
    currentIndex: currentIndex >= media.length ? 0 : currentIndex,
    slideshowMedia,
  };
}